import { useContext, useEffect, useState } from 'react'
import MainPageWrapper from '../../global/components/MainPageWrapper'
import FuturePercentBooked from '../components/future-percent-booked/FuturePercentBooked'
import { fetchData } from '../../global/utils/fetch'
import { getFuturePercentBookedData } from '../components/future-percent-booked/api'
import styles from './styles.module.css'
import { CircularProgress } from '@mui/material'
import hasPermission from '../../global/utils/user/has-permission'
import { PERMISSIONS } from '../../global/constants/permissions'
import { UserContext } from '../../base/components/base-container/BaseContainer'
import {
  DASHBOARD_KEYS,
  DOWNLOADABLE_REPORTS,
} from '../../global/constants/dashboards'
import TabChooser from './TabChooser'
import SchedulingPerformance from '../components/scheduling-performance/SchedulingPerformance'
import {
  getSchedulingPerformanceData,
  getServiceableZipCodesCsv,
} from '../components/scheduling-performance/api'
import DashboardMainHeader from '../components/header/DashBoardsMainHeader'
import DvirAuditsTable from '../components/dvir-audits/DvirAudits'
import InstallationAuditsTable from '../components/installation-audits/InstallationAudits'

interface IDashboardsMainProps {
  tabType?: DASHBOARD_KEYS
}

export interface ILocationFilters {
  locationFilter: string
  setLocationFilter: Function
}

function DashboardsMain({ tabType }: IDashboardsMainProps) {
  const { user } = useContext(UserContext)
  const [isTableLoading, setIsTableLoading] = useState<boolean>(false)
  const [isCsvLoading, setIsCsvLoading] = useState<boolean>(false)
  const [queryDate, setQueryDate] = useState<Date>(new Date())
  const [table, setTable] = useState<JSX.Element | null>(null)
  const [refetchIndex, setRefetchIndex] = useState(0)
  const [locationFilter, setLocationFilter] = useState('')

  const displayCsvButton = tabType
    ? [
        DASHBOARD_KEYS.schedulingPerformance,
        DASHBOARD_KEYS.futurePercentBooked,
      ].includes(tabType)
    : false

  const displayServiceableZipCodesButton = tabType
    ? [DASHBOARD_KEYS.futurePercentBooked].includes(tabType)
    : false

  const showLocationDropdown = tabType
    ? [DASHBOARD_KEYS.installationAudits, DASHBOARD_KEYS.dvirAudits].includes(
        tabType,
      )
    : false

  const showWeeksDropdown = tabType
    ? [DASHBOARD_KEYS.installationAudits, DASHBOARD_KEYS.dvirAudits].includes(
        tabType,
      )
    : false

  useEffect(() => {
    switch (tabType) {
      case DASHBOARD_KEYS.schedulingPerformance:
        if (
          hasPermission(user).allow(
            PERMISSIONS.DASHBOARDS.CATEGORY,
            PERMISSIONS.DASHBOARDS.ENTRIES.VIEW_SCHEDULING_PERFORMANCE.NAME,
          )
        ) {
          setTable(
            <SchedulingPerformance
              setIsTableLoading={setIsTableLoading}
              isTableLoading={isTableLoading}
              date={queryDate}
            />,
          )
        } else {
          setTable(null)
        }
        break
      case DASHBOARD_KEYS.futurePercentBooked:
        if (
          hasPermission(user).allow(
            PERMISSIONS.DASHBOARDS.CATEGORY,
            PERMISSIONS.DASHBOARDS.ENTRIES.VIEW_FUTURE_PERCENT_BOOKED.NAME,
          )
        ) {
          setTable(
            <FuturePercentBooked
              setIsTableLoading={setIsTableLoading}
              isTableLoading={isTableLoading}
              date={queryDate}
            />,
          )
        } else {
          setTable(null)
        }
        break
      case DASHBOARD_KEYS.installationAudits:
        if (
          hasPermission(user).allow(
            PERMISSIONS.DASHBOARDS.CATEGORY,
            PERMISSIONS.DASHBOARDS.ENTRIES.VIEW_INSTALLATION_AUDIT_DASHBOARD
              .NAME,
          )
        ) {
          setTable(
            <InstallationAuditsTable
              date={queryDate}
              setRefetchIndex={setRefetchIndex}
              refetchIndex={refetchIndex}
              locationFilters={{ locationFilter, setLocationFilter }}
            />,
          )
        } else {
          setTable(null)
        }
        break
      case DASHBOARD_KEYS.dvirAudits:
        if (
          hasPermission(user).allow(
            PERMISSIONS.DASHBOARDS.CATEGORY,
            PERMISSIONS.DASHBOARDS.ENTRIES.VIEW_DVIR_AUDIT_DASHBOARD.NAME,
          )
        ) {
          setTable(
            <DvirAuditsTable
              date={queryDate}
              setRefetchIndex={setRefetchIndex}
              refetchIndex={refetchIndex}
              locationFilters={{ locationFilter, setLocationFilter }}
            />,
          )
        } else {
          setTable(null)
        }
        break
      default:
        setTable(null)
    }
  }, [queryDate, locationFilter, tabType, isTableLoading])

  async function downloadCsv(input: string | undefined) {
    try {
      setIsCsvLoading(true)
      let csv: { csv: string } | undefined
      switch (input) {
        case DOWNLOADABLE_REPORTS.schedulingPerformance:
          csv = await fetchData<{ csv: string }>(
            getSchedulingPerformanceData(queryDate, true),
          )
          break
        case DOWNLOADABLE_REPORTS.futurePercentBooked:
          csv = await fetchData<{ csv: string }>(
            getFuturePercentBookedData(queryDate, true),
          )
          break
        case DOWNLOADABLE_REPORTS.serviceableZipCodes:
          csv = await fetchData<{ csv: string }>(getServiceableZipCodesCsv(queryDate))
          break
      }
      if (!csv) return
      const blob = new Blob([csv.csv], { type: 'text/csv;charset=utf-8;' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (error) {
      const errorMessage = `Error downloading CSV: ${error}`
      alert(errorMessage)
      console.error(errorMessage)
    } finally {
      setIsCsvLoading(false)
    }
  }

  return (
    <MainPageWrapper
      title='Dashboards'
      titleFields={
        <DashboardMainHeader
          queryDate={queryDate}
          setQueryDate={setQueryDate}
          setLocationFilter={setLocationFilter}
          seeLocationDropdown={showLocationDropdown}
          seeWeeksDropdown={showWeeksDropdown}
          isLoading={isTableLoading}
        />
      }
    >
      <TabChooser />
      <div className={styles.link}>
        <div className={styles.csvButtonsContainer}>
          {displayCsvButton && (
            <button
              className={styles.navLink}
              onClick={() => downloadCsv(tabType)}
              disabled={
                !hasPermission(user).allow(
                  PERMISSIONS.DASHBOARDS.CATEGORY,
                  PERMISSIONS.DASHBOARDS.ENTRIES.DOWNLOAD_CSV.NAME,
                )
              }
            >
              Download CSV
            </button>
          )}
          {displayServiceableZipCodesButton && (
            <button
              className={styles.navLink}
              onClick={() =>
                downloadCsv(DOWNLOADABLE_REPORTS.serviceableZipCodes)
              }
              disabled={
                !hasPermission(user).allow(
                  PERMISSIONS.DASHBOARDS.CATEGORY,
                  PERMISSIONS.DASHBOARDS.ENTRIES.DOWNLOAD_CSV.NAME,
                )
              }
            >
              Download Serviceable Zip Codes CSV
            </button>
          )}
          {isCsvLoading && (
            <CircularProgress
              className={styles.loadingCircle}
              size={10}
            />
          )}
        </div>
        <div className={styles.tableContainer}>
          <div>{table}</div>
        </div>
      </div>
    </MainPageWrapper>
  )
}

export default DashboardsMain
